// Lightweight in-app "data changed" bus.
// Same-tab components subscribe via subscribeDataChanged. Other tabs stay in
// sync through a localStorage marker + the cross-tab "storage" event, so every
// table, list and dashboard refreshes immediately after any mutation.

const REV_KEY = "cmsedu_data_rev_v1";

const listeners = new Set();
let storageListeners = 0;

// Which student-site endpoints must refresh when a given resource kind changes.
const STUDENT_URL_RULES = [
  { prefix: "/student/site/init", kinds: ["classes", "courses", "subjects", "chapters", "schools", "students", "school_admins", "notifications", "products", "fun_hub", "orders", "payments"] },
  { prefix: "/student/site/courses", kinds: ["classes", "courses", "subjects", "chapters", "schools", "students"] },
  { prefix: "/student/site/subjects", kinds: ["classes", "courses", "subjects", "chapters", "schools"] },
  { prefix: "/student/site/chapters", kinds: ["classes", "courses", "subjects", "chapters", "schools"] },
  { prefix: "/student/site/notifications", kinds: ["notifications"] },
  { prefix: "/student/site/products", kinds: ["products"] },
  { prefix: "/student/site/orders", kinds: ["orders"] },
  { prefix: "/student/site/fun-hub", kinds: ["fun_hub"] },
];

function notifyListeners(payload) {
  listeners.forEach((l) => {
    try { l(payload); } catch (_) {}
  });
}

function onStorage(e) {
  if (e.key !== REV_KEY) return;
  // Cross-tab change — refresh everything.
  notifyListeners({ kind: null });
}

/** Broadcast that data of `kind` changed. Call after create/update/delete/toggle/upload/import. */
export function notifyDataChanged(kind, basePath) {
  notifyListeners({ kind, basePath });
  try { localStorage.setItem(REV_KEY, String(Date.now() + Math.random())); } catch (_) {}
}

/** Subscribe to data-change events (same tab) and cross-tab storage events. Returns an unsubscribe fn. */
export function subscribeDataChanged(listener) {
  listeners.add(listener);
  if (storageListeners === 0) {
    try { window.addEventListener("storage", onStorage); } catch (_) {}
  }
  storageListeners += 1;
  return () => {
    listeners.delete(listener);
    storageListeners -= 1;
    if (storageListeners === 0) {
      try { window.removeEventListener("storage", onStorage); } catch (_) {}
    }
  };
}

/** True when a cached fetch of `url` should be invalidated after a change event. */
export function urlRelevantToChange(url, kind, basePath) {
  if (!kind) return true; // cross-tab change: refresh everything
  if (basePath && url.includes(basePath)) return true;
  for (const rule of STUDENT_URL_RULES) {
    if (url.startsWith(rule.prefix) && rule.kinds.includes(kind)) return true;
  }
  return false;
}
